import { z } from 'zod';

import { VestigasHTTPError } from '@vestigas/data';

import { USER_IDENTITY_PROVIDER_LINK_ERROR_CODES } from './constants';
import { type UserIdentityProviderLinkErrorCode } from './types';

/**
 * The body of an error response of the single sign-on link endpoints. An error without a
 * known `code`, such as the plain 404 of a route that does not exist, does not match.
 */
export const zodUserIdentityProviderLinkErrorSchema = z.object({
  detail: z.object({
    code: z.enum(USER_IDENTITY_PROVIDER_LINK_ERROR_CODES),
    message: z.string(),
  }),
});

/**
 * Whether the back-end answers with the code as a normal outcome of the request rather than
 * a fault: the user has no login, the link is already gone, or the environment may not
 * change links.
 */
const IS_EXPECTED_BY_CODE = {
  identity_provider_link_not_found: true,
  login_not_found: true,
  login_service_read_only: true,
  login_service_unavailable: false,
} as const satisfies Record<UserIdentityProviderLinkErrorCode, boolean>;

/**
 * The `detail.code` of an error response of the single sign-on link endpoints, or undefined
 * for any other error: a failure without a response, or a response without a known code.
 */
export function getUserIdentityProviderLinkErrorCode(
  error: unknown,
): UserIdentityProviderLinkErrorCode | undefined {
  if (!(error instanceof VestigasHTTPError)) {
    return undefined;
  }

  const parsed = zodUserIdentityProviderLinkErrorSchema.safeParse(
    error.parsedBody,
  );

  return parsed.success ? parsed.data.detail.code : undefined;
}

/**
 * Whether the error is a normal outcome of the request, which is final and not worth
 * reporting, as opposed to a fault or an error without a known code.
 */
export function isExpectedUserIdentityProviderLinkError(error: unknown) {
  const code = getUserIdentityProviderLinkErrorCode(error);

  return code !== undefined && IS_EXPECTED_BY_CODE[code];
}
